import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BedDouble, MapPin, ScrollText, Gift, MessageSquareWarning, MessagesSquare, Star } from "lucide-react";
import { Stars, ReviewItem } from "../components/homestay/ReviewsPanel";
import { BrandMark } from "../components/homestay/BrandingPanel";
import { useAuth } from "../context/AuthContext";
import { BRAND, errMsg, inr, today } from "../components/homestay/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cls = "rounded-xl border-2 px-3 py-2.5 text-sm w-full";

function RequestForm({ pid, authFetch }) {
  const [f, setF] = useState({ kind: "booking", plan: "monthly", check_in: today(), check_out: "", phone: "", note: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await authFetch({ method: "post", url: `${API}/homestay/public/${pid}/requests`, data: { ...f, check_out: f.check_out || null, phone: f.phone || null, note: f.note || null } });
      toast.success("Request sent — the manager will confirm your bed");
    } catch (err) { toast.error(errMsg(err, "Could not send request")); }
  };
  return (
    <form onSubmit={submit} data-testid="guest-request-form" className="grid grid-cols-2 gap-2">
      <select data-testid="guest-request-kind" className={cls} value={f.kind} onChange={set("kind")}>
        <option value="booking">Book a bed</option><option value="bed_change">Change my bed</option>
      </select>
      <select data-testid="guest-request-plan" className={cls} value={f.plan} onChange={set("plan")}>
        <option value="monthly">Monthly</option><option value="nightly">Per night</option>
      </select>
      <input type="date" required data-testid="guest-request-checkin" className={cls} value={f.check_in} onChange={set("check_in")} />
      <input type="date" data-testid="guest-request-checkout" className={cls} value={f.check_out} onChange={set("check_out")} />
      <input placeholder="WhatsApp number" data-testid="guest-request-phone" className={cls} value={f.phone} onChange={set("phone")} />
      <input placeholder="Note (lower bunk, near window…)" data-testid="guest-request-note" className={cls} value={f.note} onChange={set("note")} />
      <button data-testid="guest-request-submit" className="col-span-2 rounded-full py-3 font-bold text-white shadow-lg" style={{ background: BRAND.orange }}>Send request</button>
    </form>
  );
}

function ComplaintForm({ pid, authFetch }) {
  const [f, setF] = useState({ kind: "complaint", category: "other", text: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await authFetch({ method: "post", url: `${API}/homestay/public/${pid}/complaints`, data: f }); toast.success("Sent to the property team"); setF({ ...f, text: "" }); }
    catch (err) { toast.error(errMsg(err, "Could not send")); }
  };
  return (
    <form onSubmit={submit} data-testid="guest-complaint-form" className="grid grid-cols-2 gap-2">
      <select data-testid="guest-complaint-kind" className={cls} value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
        <option value="complaint">Complaint</option><option value="suggestion">Suggestion</option>
      </select>
      <select data-testid="guest-complaint-category" className={cls} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
        {["cleanliness", "food", "water", "electricity", "wifi", "safety", "staff", "noise", "other"].map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <textarea required minLength={5} rows={3} data-testid="guest-complaint-text" className={`${cls} col-span-2`} placeholder="Tell us what happened…" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} />
      <button data-testid="guest-complaint-submit" className="col-span-2 rounded-full py-2.5 font-bold text-white" style={{ background: BRAND.green }}>Send</button>
    </form>
  );
}

const Card = ({ icon: Icon, title, children, tid }) => (
  <div data-testid={tid} className="rounded-3xl border-2 bg-white p-5" style={{ borderColor: BRAND.line }}>
    <div className="text-sm font-bold inline-flex items-center gap-1.5 mb-3" style={{ color: BRAND.green }}><Icon className="w-4 h-4" style={{ color: BRAND.orange }} /> {title}</div>
    {children}
  </div>
);

function PublicReviews({ pid }) {
  const [d, setD] = useState({ items: [] });
  useEffect(() => { axios.get(`${API}/homestay/public/${pid}/reviews`).then((r) => setD(r.data)).catch(() => {}); }, [pid]);
  if (!d.items.length) return null;
  return <Card icon={Star} title={`Guest reviews (${d.rating_count})`} tid="public-reviews">{d.items.slice(0, 10).map((rv) => <ReviewItem key={rv.id} rv={rv} />)}</Card>;
}

export default function HomeStayProperty() {
  const { pid } = useParams();
  const { user, authFetch } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { axios.get(`${API}/homestay/public/${pid}`).then((r) => setP(r.data)).catch(() => setErr("This property isn't live yet.")); }, [pid]);
  const openChat = async () => {
    if (!user) return nav(`/login?next=/homestay/p/${pid}`);
    try { const r = await authFetch({ method: "post", url: `${API}/homestay/public/${pid}/chat` }); nav(`/homestay/my-stay?chat=${r.data.id}`); }
    catch (e) { toast.error(errMsg(e, "Could not open chat")); }
  };

  if (err) return <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="public-property-error">{err}</div>;
  if (!p) return <div className="max-w-3xl mx-auto px-4 py-16 text-sm" style={{ color: BRAND.muted }}>Loading…</div>;
  const loginCta = <Link to={`/login?next=/homestay/p/${pid}`} data-testid="public-login-cta" className="inline-block rounded-full px-5 py-2.5 font-bold text-white text-sm" style={{ background: BRAND.orange }}>Log in to continue</Link>;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-5" data-testid="public-property-page">
      <div>
        <span className="chip text-[10px]">{p.property_type.toUpperCase()}</span>
        <div className="mt-2 flex items-center gap-3">
          <BrandMark property={p} size={64} tid="public-brand-logo" />
          <div className="min-w-0">
            <h1 className="font-display text-4xl sm:text-5xl font-bold" style={{ color: BRAND.green }} data-testid="public-property-name">{p.brand_name || p.name}</h1>
            {p.tagline && <div className="text-sm" style={{ color: BRAND.muted }} data-testid="public-brand-tagline">{p.tagline}</div>}
          </div>
        </div>
        <div className="mt-1 text-sm inline-flex items-center gap-1" style={{ color: BRAND.muted }}><MapPin className="w-4 h-4" /> {p.locality}, {p.city}</div>
        {p.rating_count > 0 && <div className="mt-2 text-sm font-bold inline-flex items-center gap-1.5" data-testid="public-rating"><Stars value={p.rating_avg} size={16} /> {p.rating_avg} · {p.rating_count} reviews</div>}
        <div className="mt-3 text-sm font-bold" data-testid="public-vacant-count"><span className="bed-blink inline-block w-2.5 h-2.5 rounded-full mr-1.5" style={{ background: "#16A34A" }} />{p.vacant} of {p.total} beds free right now</div>
        <button onClick={openChat} data-testid="public-chat-btn" className="mt-4 rounded-full px-5 py-2.5 text-sm font-bold text-white inline-flex items-center gap-2 shadow-lg transition-transform hover:-translate-y-0.5" style={{ background: BRAND.green }}>
          <MessagesSquare className="w-4 h-4" /> Chat with the property team
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="public-rooms">
        {p.rooms.map((r) => (
          <div key={r.id} data-testid={`public-room-${r.name}`} className="rounded-2xl border-2 bg-white p-4" style={{ borderColor: r.vacant ? "#16A34A55" : BRAND.line }}>
            <div className="font-bold inline-flex items-center gap-1.5" style={{ color: BRAND.green }}><BedDouble className="w-4 h-4" /> {r.name}</div>
            <div className="text-xs mt-1" style={{ color: BRAND.muted }}>Floor {r.floor} · {r.vacant}/{r.total} free</div>
            <div className="text-sm mt-2"><b>{inr(r.monthly_from)}</b>/month · {inr(r.nightly_from)}/night</div>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card icon={BedDouble} title="Request a bed / change bed" tid="public-request-card">{user ? <RequestForm pid={pid} authFetch={authFetch} /> : loginCta}</Card>
        <Card icon={MessageSquareWarning} title="Complaint or suggestion" tid="public-complaint-card">{user ? <ComplaintForm pid={pid} authFetch={authFetch} /> : loginCta}</Card>
        {p.house_rules && <Card icon={ScrollText} title="House rules" tid="public-house-rules"><p className="text-sm whitespace-pre-wrap">{p.house_rules}</p></Card>}
        <PublicReviews pid={pid} />
        {p.kit_items?.length > 0 && <Card icon={Gift} title="Your welcome kit" tid="public-kit"><ul className="text-sm list-disc pl-5 space-y-1">{p.kit_items.map((k) => <li key={k}>{k}</li>)}</ul></Card>}
      </div>
      {user && <Link to="/homestay/my-stay" data-testid="public-my-stay-link" className="inline-block text-sm font-bold" style={{ color: BRAND.green }}>See my stay & request status →</Link>}
    </div>
  );
}
